import React, { useState } from 'react';
import { 
  Settings, Database, Server, RefreshCw, Trash2, Download,
  Upload, AlertTriangle, CheckCircle2, Clock, HardDrive,
  Activity, Zap, Shield, Bell, Mail, FileSpreadsheet, 
  FileDown, FileUp, Package, Users, ShoppingCart, Loader2,
  CheckCircle, XCircle, Table, Check, ChevronDown, FolderOpen
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { csvAPI } from '../services/api';

// Field definitions for each data type
const FIELD_DEFINITIONS = {
  products: [
    { id: 'id', label: 'ID', required: false },
    { id: 'name', label: 'Product Name', required: true },
    { id: 'price', label: 'Price', required: true },
    { id: 'category', label: 'Category', required: false },
    { id: 'barcode', label: 'Barcode', required: false },
    { id: 'taxable', label: 'Taxable', required: false },
    { id: 'in_stock', label: 'In Stock', required: false },
    { id: 'region', label: 'Region', required: false },
    { id: 'image', label: 'Image URL', required: false },
  ],
  orders: [
    { id: 'id', label: 'Order ID', required: false },
    { id: 'user_id', label: 'User ID', required: false },
    { id: 'status', label: 'Status', required: false },
    { id: 'payment_status', label: 'Payment Status', required: false },
    { id: 'payment_method', label: 'Payment Method', required: false },
    { id: 'subtotal', label: 'Subtotal', required: false },
    { id: 'tax', label: 'Tax', required: false },
    { id: 'delivery_fee', label: 'Delivery Fee', required: false },
    { id: 'total', label: 'Total', required: false },
    { id: 'created_at', label: 'Created At', required: false },
  ],
  users: [
    { id: 'id', label: 'User ID', required: false },
    { id: 'name', label: 'Name', required: true },
    { id: 'email', label: 'Email', required: true },
    { id: 'phone', label: 'Phone', required: false },
    { id: 'role', label: 'Role', required: false },
    { id: 'status', label: 'Status', required: false },
    { id: 'order_count', label: 'Order Count', required: false },
    { id: 'created_at', label: 'Created At', required: false },
  ],
};

const SystemMaintenance = () => {
  const [isClearing, setIsClearing] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    autoBackup: true,
    emailNotifications: true,
    debugMode: false,
  });

  // CSV Manager state
  const [csvExportType, setCsvExportType] = useState('products');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState('products');
  const [importResult, setImportResult] = useState(null);
  
  // Field selection state
  const [exportAllFields, setExportAllFields] = useState(true);
  const [selectedExportFields, setSelectedExportFields] = useState({});
  const [importAllFields, setImportAllFields] = useState(true);
  const [selectedImportFields, setSelectedImportFields] = useState({});
  const [showExportFieldSelector, setShowExportFieldSelector] = useState(false);
  const [showImportFieldSelector, setShowImportFieldSelector] = useState(false);
  const [exportFilename, setExportFilename] = useState('');

  // Initialize selected fields when type changes
  const initializeFields = (type, setSelected) => {
    const fields = FIELD_DEFINITIONS[type] || [];
    const initial = {};
    fields.forEach(f => { initial[f.id] = true; });
    setSelected(initial);
  };

  // Handle export type change
  const handleExportTypeChange = (type) => {
    setCsvExportType(type);
    initializeFields(type, setSelectedExportFields);
    setExportFilename(`${type}_export_${new Date().toISOString().split('T')[0]}`);
  };

  // Handle import type change  
  const handleImportTypeChange = (type) => {
    setImportType(type);
    initializeFields(type, setSelectedImportFields);
  };

  // Toggle field selection
  const toggleExportField = (fieldId) => {
    setSelectedExportFields(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  const toggleImportField = (fieldId) => {
    setSelectedImportFields(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  // Select/Deselect all fields
  const selectAllExportFields = (select) => {
    const fields = FIELD_DEFINITIONS[csvExportType] || [];
    const updated = {};
    fields.forEach(f => { updated[f.id] = select; });
    setSelectedExportFields(updated);
  };

  const selectAllImportFields = (select) => {
    const fields = FIELD_DEFINITIONS[importType] || [];
    const updated = {};
    fields.forEach(f => { updated[f.id] = select; });
    setSelectedImportFields(updated);
  };

  // Mock system stats
  const systemStats = {
    uptime: '15 days, 6 hours',
    lastBackup: '2024-12-30 03:00 AM',
    databaseSize: '245 MB',
    cacheSize: '12 MB',
    totalOrders: 1247,
    totalProducts: 375,
    totalUsers: 89,
  };

  const handleClearCache = async () => {
    setIsClearing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsClearing(false);
    toast.success('Cache cleared successfully');
  };

  const handleBackup = async () => {
    setIsBackingUp(true);
    await new Promise(resolve => setTimeout(resolve, 3000));
    setIsBackingUp(false);
    toast.success('Backup completed successfully');
  };

  const handleToggleSetting = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    toast.success('Setting updated');
  };

  // CSV Export handler with field selection and Save As dialog
  const handleExportCSV = async (useSaveAs = false) => {
    // Get selected fields
    const fields = exportAllFields 
      ? FIELD_DEFINITIONS[csvExportType].map(f => f.id)
      : Object.keys(selectedExportFields).filter(k => selectedExportFields[k]);
    
    if (fields.length === 0) {
      toast.error('Please select at least one field to export');
      return;
    }

    setIsExporting(true);
    try {
      const blob = await csvAPI.export(csvExportType, fields);
      const filename = `${exportFilename || csvExportType + '_export'}.csv`;
      
      // Try to use File System Access API for "Save As" dialog
      if (useSaveAs) {
        // Check if we're on a device that supports File System Access API
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const supportsFilePicker = typeof window.showSaveFilePicker === 'function';
        
        // Mobile devices don't support showSaveFilePicker
        if (isMobile) {
          toast.info('Save As is not available on mobile devices. Downloading to your device instead.');
          downloadFile(blob, filename);
          setIsExporting(false);
          return;
        }
        
        if (!supportsFilePicker) {
          toast.info('Save As requires Chrome, Edge, or Opera browser. Using regular download.');
          downloadFile(blob, filename);
          setIsExporting(false);
          return;
        }
        
        try {
          const options = {
            suggestedName: filename,
            types: [{
              description: 'CSV Spreadsheet',
              accept: { 
                'text/csv': ['.csv']
              }
            }],
            excludeAcceptAllOption: false
          };
          
          console.log('Opening Save As dialog with options:', options);
          const handle = await window.showSaveFilePicker(options);
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          toast.success('File saved successfully!');
          setIsExporting(false);
          return;
        } catch (err) {
          console.warn('Save As error:', err.name, err.message);
          
          if (err.name === 'AbortError') {
            // User cancelled the dialog
            toast.info('Save cancelled');
            setIsExporting(false);
            return;
          }
          
          if (err.name === 'SecurityError') {
            toast.error('Security error: Save As requires a secure connection (HTTPS) or localhost.');
            downloadFile(blob, filename);
            setIsExporting(false);
            return;
          }
          
          // Other errors - fall through to regular download
          toast.info('Could not open Save dialog. Downloading to Downloads folder.');
          downloadFile(blob, filename);
          setIsExporting(false);
          return;
        }
      }
      
      // Regular download to Downloads folder
      downloadFile(blob, filename);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV: ' + (error.message || 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to download file
  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success(`${csvExportType} exported to Downloads folder!`);
  };

  // CSV Import handler with field selection
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    // Get selected fields for import mapping
    const fields = importAllFields 
      ? FIELD_DEFINITIONS[importType].map(f => f.id)
      : Object.keys(selectedImportFields).filter(k => selectedImportFields[k]);
    
    if (fields.length === 0) {
      toast.error('Please select at least one field to import');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const result = await csvAPI.import(importType, importFile, fields);
      setImportResult(result);
      toast.success(`Successfully imported ${result.imported} records!`);
      setImportFile(null);
      // Clear the file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({ error: error.message });
      toast.error(error.message || 'Failed to import CSV');
    } finally {
      setIsImporting(false);
    }
  };

  // File input handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportResult(null);
    } else if (file) {
      toast.error('Please select a valid CSV file');
    }
  };

  // Download sample CSV template
  const handleDownloadTemplate = (type) => {
    let csvContent = '';
    
    switch(type) {
      case 'products':
        csvContent = 'name,price,category,barcode,taxable,inStock,image\n';
        csvContent += 'Sample Product,9.99,spices,1234567890123,false,true,https://example.com/image.jpg\n';
        break;
      case 'users':
        csvContent = 'name,email,phone,role\n';
        csvContent += 'John Doe,john@example.com,+1234567890,customer\n';
        break;
      default:
        csvContent = 'column1,column2\nvalue1,value2\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    toast.success('Template downloaded');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Settings className="w-8 h-8 text-gray-500" />
            System Maintenance
          </h1>
          <p className="text-gray-500 mt-1">Manage system settings, backups, and data</p>
        </div>
        {settings.maintenanceMode && (
          <Badge className="bg-yellow-100 text-yellow-700 px-4 py-2">
            <AlertTriangle className="w-4 h-4 mr-2 inline" />
            Maintenance Mode Active
          </Badge>
        )}
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">System Status</p>
              <p className="font-bold text-green-600">Online</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Uptime</p>
              <p className="font-bold text-gray-800">{systemStats.uptime}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Database className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Database Size</p>
              <p className="font-bold text-gray-800">{systemStats.databaseSize}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cache Size</p>
              <p className="font-bold text-gray-800">{systemStats.cacheSize}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* CSV Manager Section */}
      <Card className="p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-green-600" />
          CSV Data Manager
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Export Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-700">
              <FileDown className="w-5 h-5 text-blue-500" />
              Export Data
            </div>
            <p className="text-sm text-gray-500">Download your data as CSV files to your device</p>
            
            <div className="p-4 bg-blue-50 rounded-xl space-y-4">
              <div>
                <Label>Select Data Type</Label>
                <Select value={csvExportType} onValueChange={handleExportTypeChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Products ({systemStats.totalProducts})
                      </div>
                    </SelectItem>
                    <SelectItem value="orders">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4" />
                        Orders ({systemStats.totalOrders})
                      </div>
                    </SelectItem>
                    <SelectItem value="users">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Users ({systemStats.totalUsers})
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Field Selection Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="exportAllFields"
                    checked={exportAllFields}
                    onCheckedChange={setExportAllFields}
                  />
                  <Label htmlFor="exportAllFields" className="cursor-pointer">Export all fields</Label>
                </div>
                {!exportAllFields && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowExportFieldSelector(!showExportFieldSelector)}
                    className="text-blue-600"
                  >
                    <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${showExportFieldSelector ? 'rotate-180' : ''}`} />
                    Select Fields
                  </Button>
                )}
              </div>

              {/* Field Selector */}
              {!exportAllFields && showExportFieldSelector && (
                <div className="bg-white rounded-lg p-3 border border-blue-200 space-y-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">Select fields to export:</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => selectAllExportFields(true)}>
                        All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => selectAllExportFields(false)}>
                        None
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {FIELD_DEFINITIONS[csvExportType]?.map((field) => (
                      <div key={field.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`export-${field.id}`}
                          checked={selectedExportFields[field.id] || false}
                          onCheckedChange={() => toggleExportField(field.id)}
                        />
                        <Label htmlFor={`export-${field.id}`} className="text-sm cursor-pointer">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filename Input */}
              <div>
                <Label>File Name</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={exportFilename}
                    onChange={(e) => setExportFilename(e.target.value)}
                    placeholder={`${csvExportType}_export`}
                    className="flex-1"
                  />
                  <span className="self-center text-gray-500">.csv</span>
                </div>
              </div>

              {/* Export Buttons */}
              <div className="space-y-2">
                <Button 
                  onClick={() => handleExportCSV(true)}
                  disabled={isExporting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="export-csv-save-as-btn"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Save As... (Desktop Only)
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={() => handleExportCSV(false)}
                  disabled={isExporting}
                  variant="outline"
                  className="w-full"
                  data-testid="export-csv-btn"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Quick Download
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  💡 &quot;Save As&quot; works on Chrome/Edge desktops. Mobile devices use Quick Download.
                </p>
              </div>
            </div>
          </div>

          {/* Import Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-700">
              <FileUp className="w-5 h-5 text-green-500" />
              Import Data
            </div>
            <p className="text-sm text-gray-500">Upload CSV files to import data</p>
            
            <div className="p-4 bg-green-50 rounded-xl space-y-4">
              <div>
                <Label>Import Type</Label>
                <Select value={importType} onValueChange={handleImportTypeChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Products
                      </div>
                    </SelectItem>
                    <SelectItem value="users">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Users
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Field Selection Toggle for Import */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="importAllFields"
                    checked={importAllFields}
                    onCheckedChange={setImportAllFields}
                  />
                  <Label htmlFor="importAllFields" className="cursor-pointer">Import all fields from CSV</Label>
                </div>
                {!importAllFields && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowImportFieldSelector(!showImportFieldSelector)}
                    className="text-green-600"
                  >
                    <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${showImportFieldSelector ? 'rotate-180' : ''}`} />
                    Map Fields
                  </Button>
                )}
              </div>

              {/* Field Selector for Import */}
              {!importAllFields && showImportFieldSelector && (
                <div className="bg-white rounded-lg p-3 border border-green-200 space-y-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">Select fields to import:</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => selectAllImportFields(true)}>
                        All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => selectAllImportFields(false)}>
                        None
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {FIELD_DEFINITIONS[importType]?.map((field) => (
                      <div key={field.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`import-${field.id}`}
                          checked={selectedImportFields[field.id] || false}
                          onCheckedChange={() => toggleImportField(field.id)}
                          disabled={field.required}
                        />
                        <Label htmlFor={`import-${field.id}`} className={`text-sm cursor-pointer ${field.required ? 'text-gray-400' : ''}`}>
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">* (required)</span>}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Select CSV File</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="flex-1"
                  />
                </div>
                {importFile && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {importFile.name} selected
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleImportCSV}
                  disabled={isImporting || !importFile}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleDownloadTemplate(importType)}
                >
                  <Table className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </div>

              {/* Import Result */}
              {importResult && (
                <div className={`p-3 rounded-lg ${importResult.error ? 'bg-red-100' : 'bg-green-100'}`}>
                  {importResult.error ? (
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="w-5 h-5" />
                      <span>{importResult.error}</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Import Successful!</span>
                      </div>
                      <p className="text-sm text-green-600">
                        {importResult.imported} records imported
                        {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
                        {importResult.errors > 0 && `, ${importResult.errors} errors`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CSV Tips */}
        <div className="mt-6 p-4 bg-gray-50 rounded-xl">
          <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            CSV Import Tips
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Download the template first to see the required format</li>
            <li>• First row must contain column headers</li>
            <li>• For products: name, price, category are required fields</li>
            <li>• For users: name, email are required fields</li>
            <li>• Existing records with same ID/barcode will be updated</li>
          </ul>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Quick Actions
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-gray-800">Clear Cache</p>
                  <p className="text-sm text-gray-500">Remove temporary files</p>
                </div>
              </div>
              <Button 
                onClick={handleClearCache}
                disabled={isClearing}
                variant="outline"
              >
                {isClearing ? 'Clearing...' : 'Clear'}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-800">Backup Database</p>
                  <p className="text-sm text-gray-500">Last: {systemStats.lastBackup}</p>
                </div>
              </div>
              <Button 
                onClick={handleBackup}
                disabled={isBackingUp}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {isBackingUp ? 'Backing up...' : 'Backup Now'}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Trash2 className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium text-gray-800">Clear All Data</p>
                  <p className="text-sm text-gray-500">Permanently delete all data</p>
                </div>
              </div>
              <Button 
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-100"
              >
                Clear
              </Button>
            </div>
          </div>
        </Card>

        {/* System Settings */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            System Settings
          </h2>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="font-medium text-gray-800">Maintenance Mode</p>
                  <p className="text-sm text-gray-500">Disable site access</p>
                </div>
              </div>
              <Switch
                checked={settings.maintenanceMode}
                onCheckedChange={() => handleToggleSetting('maintenanceMode')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium text-gray-800">Auto Backup</p>
                  <p className="text-sm text-gray-500">Daily at 3:00 AM</p>
                </div>
              </div>
              <Switch
                checked={settings.autoBackup}
                onCheckedChange={() => handleToggleSetting('autoBackup')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-gray-800">Email Notifications</p>
                  <p className="text-sm text-gray-500">System alerts</p>
                </div>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={() => handleToggleSetting('emailNotifications')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium text-gray-800">Debug Mode</p>
                  <p className="text-sm text-gray-500">Detailed logging</p>
                </div>
              </div>
              <Switch
                checked={settings.debugMode}
                onCheckedChange={() => handleToggleSetting('debugMode')}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SystemMaintenance;
